import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, View } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { getWeekStart } from '../utils/date';
import { resolveWeekStartsOn } from '@shared/meals';
import { selectedUserIdSelector, setSelectedUserId } from '../redux/appSlice';
// Common components (lightweight, keep in main bundle)
import { ContentView } from '../components/ContentView';
import { Glyph } from '../components/Glyph';
import { PromptText } from '../components/PromptText';
import { SidePanel } from '../components/SidePanel';
import { OptionalResponsiveFormProvider, ResponsiveFormProvider, } from '../intakeForm/ResponsiveFormContext';
import { Button } from '../components/Button';
import { ClientSettingsButton, ClientSettingsMenu, } from '../components/ClientSettingsMenu';
import { SettingsView } from '../components/SettingsView';
import { UserInfo } from '../components/UserInfo';
import { AdminBackButton, AdminClientTitle, } from '../components/AdminPhoneHeader';
import { useIsNarrowWindow, usePhoneOrientation, } from '../utils/useIsNarrowWindow';
import { RotatePhoneHint } from '../components/RotatePhoneHint';
import { useCreateUserEventMutation, useGetMeQuery, useGetUserQuery, useUpdateUserMutation, } from '../services/abbiWayApi';
// Notes / Photos (used as overlays, not tab content)
import { NoteSidePanel } from '../crm/NoteSidePanel';
import PhotoUploadModal from '../photos/PhotoUploadModal.web';
import { SummaryHeaderTitle } from '../components/SummaryHeaderTitle';
import { MealMapHeaderTitle } from '../components/MealMapHeaderTitle';
import { dateWithNoTimeDashed } from '../utils/date';
import { useToday } from '../utils/useToday';
import { useAnchoredDate } from '../utils/useAnchoredDate';
import { ADMIN_HOME_TABS, CLIENT_HOME_TABS, adminPhoneView, resolveInitialHomeTab, shouldShowSidePanel, tabFromPathname, } from '../utils/homeTabs';
// Tab content: lazy-loaded so each tab is a separate chunk (smaller initial load)
const LazyProfileView = React.lazy(() => import('../intakeForm/ProfileView').then(m => ({ default: m.ProfileView })));
const LazyAdminMealMapView = React.lazy(() => import('../components/AdminMealMapView').then(m => ({
    default: m.AdminMealMapView,
})));
const LazySummaryView = React.lazy(() => import('../components/SummaryView').then(m => ({ default: m.SummaryView })));
const LazyTrendsView = React.lazy(() => import('../components/TrendsView').then(m => ({ default: m.TrendsView })));
const LazyHistoryView = React.lazy(() => import('../crm/HistoryView').then(m => ({ default: m.HistoryView })));
const LazyPhotosView = React.lazy(() => import('../photos/PhotosView').then(m => ({ default: m.PhotosView })));
const TabFallback = () => (<View style={tabFallbackStyles.container}>
    <ActivityIndicator size="large"/>
  </View>);
/**
 * Wide tabs (Meal Map, Summary, Trends) get the "turn sideways" hint on top
 * when a phone is held upright (#407).
 */
const WithRotateHint = ({ children }) => (<View style={tabFallbackStyles.withHint}>
    <RotatePhoneHint />
    {children}
  </View>);
const Tab = createBottomTabNavigator();
const MealMapIcon = () => <Glyph name="calendar_view_month"/>;
const NoteIcon = () => <Glyph name="notes"/>;
const ProfileIcon = () => <Glyph name="person"/>;
const PhotosIcon = () => <Glyph name="photo_camera"/>; // TODO
const SummaryIcon = () => <Glyph name="assessment"/>;
const TrendsIcon = () => <Glyph name="trending_up"/>;
export const HomeScreen = () => {
    const navigation = useNavigation();
    const { data: me, isLoading: isMeLoading } = useGetMeQuery();
    const dispatch = useDispatch();
    const isSignedIn = me !== undefined;
    const isAdmin = Boolean(me && me.role.name === 'Admin');
    const selectedUserId = useSelector(selectedUserIdSelector);
    // Clients on a phone-width window: no 300px side panel; its contents move
    // into a header menu so the tabs get the full width. Admin is unchanged.
    const isNarrowWindow = useIsNarrowWindow();
    // Clients get the Settings header at every width (no side panel), so the
    // Meal Map has the full window; admins keep the side panel.
    const clientHeader = isSignedIn && !isAdmin;
    const narrowClient = isNarrowWindow && clientHeader;
    // Sideways phone (~390px tall): hide the "Abbi's Way" title bar and slim
    // the tab header and tab bar so the content gets the height (#407).
    // Desktops, tablets and native are unaffected.
    const { isLandscapePhone } = usePhoneOrientation();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const renderSettingsButton = useCallback(() => (<ClientSettingsButton open={settingsOpen} onPress={() => setSettingsOpen(o => !o)}/>), [settingsOpen]);
    // Client Settings icon sits in the "Abbi's Way" title bar, left of the
    // title; no separate header row. Admins keep the default header. On a
    // landscape phone the title bar and icon are hidden; Settings returns in
    // portrait.
    useEffect(() => {
        if (isLandscapePhone) {
            setSettingsOpen(false);
        }
    }, [isLandscapePhone]);
    const [switcherOpen, setSwitcherOpen] = useState(false);
    // GET /users/:id is admin-only. Clients must use `me` from GET /auth/me.
    const skipUserDetailFetch = !selectedUserId ||
        !me ||
        (me.role.name !== 'Admin' && selectedUserId === me.id);
    const { data: selectedUserFromApi, error: selectedUserError, isLoading: isSelectedUserLoading, isFetching: isSelectedUserFetching, isError: isSelectedUserFetchError, } = useGetUserQuery(selectedUserId, {
        skip: skipUserDetailFetch,
    });
    const selectedUserInfo = me &&
        me.role.name !== 'Admin' &&
        selectedUserId != null &&
        selectedUserId === me.id
        ? me
        : selectedUserFromApi;
    useEffect(() => {
        if (me && me.role.name !== 'Admin') {
            dispatch(setSelectedUserId(me.id));
        }
    }, [me, dispatch]);
    const isViewingSelectedUser = selectedUserInfo?.id !== undefined && selectedUserInfo.id !== me?.id;
    // Admin on a phone (#411): client list screen, then the client's tabs with
    // a back arrow + name (tap to switch); Settings behind the person icon.
    const adminView = adminPhoneView({
        isAdmin,
        isNarrowWindow,
        isLandscapePhone,
        hasClient: selectedUserId != null && selectedUserId !== me?.id,
    });
    const phoneAdmin = adminView !== 'none';
    // Phones: Add Note and Upload Photo open as full-screen sheets (#411).
    const phoneSheet = isNarrowWindow || isLandscapePhone;
    const settingsHeader = clientHeader || phoneAdmin;
    useEffect(() => {
        if (!settingsHeader) {
            setSettingsOpen(false);
        }
    }, [settingsHeader]);
    useEffect(() => {
        if (adminView !== 'client' || isLandscapePhone) {
            setSwitcherOpen(false);
        }
    }, [adminView, isLandscapePhone]);
    // A phone admin lands on the client list, unless a deep link names a tab.
    const landedRef = useRef(false);
    useEffect(() => {
        if (landedRef.current || !me) {
            return;
        }
        landedRef.current = true;
        const path = typeof window !== 'undefined' ? window.location?.pathname : undefined;
        if (phoneAdmin && !tabFromPathname(path)) {
            dispatch(setSelectedUserId(undefined));
        }
    }, [me, phoneAdmin, dispatch]);
    const backToClientList = useCallback(() => {
        setSwitcherOpen(false);
        setSettingsOpen(false);
        dispatch(setSelectedUserId(undefined));
    }, [dispatch]);
    const clientName = selectedUserInfo
        ? `${selectedUserInfo.firstName ?? ''} ${selectedUserInfo.lastName ?? ''}`.trim()
        : '';
    // Client Settings icon sits in the "Abbi's Way" title bar, left of the
    // title. Phone admins: Settings left on the list; back arrow left, client
    // name title and Settings right on a client. Sideways phones hide the
    // title bar (#408).
    useLayoutEffect(() => {
        const onClient = adminView === 'client';
        navigation.setOptions({
            headerShown: !isLandscapePhone,
            headerLeft: isLandscapePhone
                ? undefined
                : onClient
                    ? () => <AdminBackButton onPress={backToClientList}/>
                    : settingsHeader
                        ? renderSettingsButton
                        : undefined,
            headerTitle: onClient && clientName
                ? () => (<AdminClientTitle name={clientName} open={switcherOpen} onPress={() => {
                        setSettingsOpen(false);
                        setSwitcherOpen(o => !o);
                    }}/>)
                : undefined,
            headerRight: onClient && !isLandscapePhone ? renderSettingsButton : undefined,
        });
    }, [
        navigation,
        adminView,
        settingsHeader,
        isLandscapePhone,
        renderSettingsButton,
        backToClientList,
        clientName,
        switcherOpen,
    ]);
    // Sideways: no Settings icon (rotate to portrait); phone admins get a back
    // button with the client's name in the slim tab header.
    const backLabel = selectedUserInfo?.firstName ?? clientName;
    const renderLandscapeBack = useCallback(() => <AdminBackButton onPress={backToClientList} label={backLabel}/>, [backToClientList, backLabel]);
    const landscapeTabOptions = useMemo(() => adminView === 'client'
        ? { ...shortLandscapeTabOptions, headerLeft: renderLandscapeBack }
        : shortLandscapeTabOptions, [adminView, renderLandscapeBack]);
    const showAdminMealArea = isViewingSelectedUser &&
        Boolean(selectedUserInfo && selectedUserInfo.programType !== null);
    const showClientMealArea = !isAdmin &&
        Boolean(selectedUserInfo && selectedUserInfo.programType !== null && me);
    const isProfileLoading = isMeLoading ||
        (!skipUserDetailFetch && (isSelectedUserLoading || isSelectedUserFetching));
    const showSelectedUserFetchError = !skipUserDetailFetch && isSelectedUserFetchError;
    // State for adding a new client
    const [isAddingClient, setIsAddingClient] = useState(false);
    // Shared date state for MealMap and Summary tabs. `endDate` is "today" (the
    // current week's Monday), derived from useToday so it auto-advances when the
    // tab is refocused or the clock rolls past midnight into a new week.
    const today = useToday();
    const weekStartsOn = resolveWeekStartsOn(selectedUserInfo?.weekStartsOn);
    const endDate = useMemo(() => getWeekStart(today, weekStartsOn), [today, weekStartsOn]);
    const startDate = selectedUserInfo?.signupDate
        ? getWeekStart(new Date(selectedUserInfo.signupDate), weekStartsOn)
        : endDate;
    const [currentDate, setCurrentDate] = useAnchoredDate(endDate);
    const monthIndex = (d) => d.getFullYear() * 12 + d.getMonth();
    const backDisabled = !!startDate && monthIndex(currentDate) <= monthIndex(startDate);
    const fwdDisabled = monthIndex(currentDate) >= monthIndex(endDate);
    // Persist selected tab across page refreshes and client changes
    // URL tab (/Home/<Tab>) wins; the saved tab is only a fallback (#404).
    const currentPathname = () => typeof window !== 'undefined' ? window.location?.pathname : undefined;
    const savedTab = (key) => typeof window !== 'undefined' && window.localStorage
        ? localStorage.getItem(key)
        : null;
    const getInitialTab = () => resolveInitialHomeTab({
        pathname: currentPathname(),
        saved: savedTab('adminSelectedTab'),
        allowed: ADMIN_HOME_TABS,
        fallback: 'Profile',
    });
    const [currentTab, setCurrentTab] = useState(getInitialTab);
    const getClientInitialTab = () => resolveInitialHomeTab({
        pathname: currentPathname(),
        saved: savedTab('clientSelectedTab'),
        allowed: CLIENT_HOME_TABS,
        fallback: 'Meal Map',
    });
    const [clientTab, setClientTab] = useState(getClientInitialTab);
    // Update localStorage when tab changes
    useEffect(() => {
        if (typeof window !== 'undefined' && window.localStorage && isAdmin) {
            localStorage.setItem('adminSelectedTab', currentTab);
        }
    }, [currentTab, isAdmin]);
    useEffect(() => {
        if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem('clientSelectedTab', clientTab);
        }
    }, [clientTab]);
    // Stable render function for Trends tab to avoid "Rendered more hooks than previous render"
    // (inline children={() => ...} created new component type each render, causing hook mismatches)
    const renderTrendsTab = useCallback(() => selectedUserInfo ? (<WithRotateHint>
          <Suspense fallback={<TabFallback />}>
            <LazyTrendsView userId={selectedUserInfo.id} clientFirstName={selectedUserInfo.firstName || ''} _onChangeDate={setCurrentDate}/>
          </Suspense>
        </WithRotateHint>) : (<TabFallback />), [selectedUserInfo, setCurrentDate]);
    const handleAddClientPress = () => {
        dispatch(setSelectedUserId(undefined)); // Deselect current client
        setIsAddingClient(true);
    };
    const handleAddClientComplete = (newlyCreatedUser) => {
        setIsAddingClient(false);
        if (newlyCreatedUser) {
            dispatch(setSelectedUserId(newlyCreatedUser.id));
        }
    };
    const handleCancelAdd = () => {
        setIsAddingClient(false);
    };
    const [notePanelMounted, setNotePanelMounted] = useState(false);
    const [notePanelVisible, setNotePanelVisible] = useState(false);
    const closeNotePanel = () => setNotePanelVisible(false);
    const handleNotePanelAnimationComplete = () => {
        if (!notePanelVisible) {
            setNotePanelMounted(false);
        }
    };
    const [createNote] = useCreateUserEventMutation();
    const addNoteButton = () => (<View style={[styles.headerButton, phoneAdmin && styles.headerButtonPhone]}>
      <Button title="Add Note" onPress={() => {
            setNotePanelMounted(true);
            setNotePanelVisible(true);
        }}/>
    </View>);
    const [editingProfile, setEditingProfile] = useState(false);
    const editProfileButton = () => (<View style={[styles.headerButton, phoneAdmin && styles.headerButtonPhone]}>
      <Button title={editingProfile ? 'Cancel Editing' : 'Edit Profile'} onPress={() => setEditingProfile(rev => !rev)}/>
    </View>);
    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
    const uploadPhotoButton = () => (<View style={[styles.headerButton, phoneAdmin && styles.headerButtonPhone]}>
      <Button title="Upload Photo" onPress={() => setIsUploadModalVisible(true)} // Open the modal
    />
    </View>);
    const [updateUser] = useUpdateUserMutation();
    const editCompletion = (payload) => {
        if (selectedUserInfo) {
            updateUser({ payload, userId: selectedUserInfo.id }).finally(() => setEditingProfile(false));
        }
    };
    useEffect(() => {
        if (showSelectedUserFetchError && selectedUserError) {
            console.error('HomeScreen: Failed to fetch details for selected user:', selectedUserId, selectedUserError);
        }
    }, [showSelectedUserFetchError, selectedUserError, selectedUserId]);
    const renderMealMapHeader = useCallback(() => (<MealMapHeaderTitle date={currentDate} onDateChange={setCurrentDate} backDisabled={dateWithNoTimeDashed(currentDate) === dateWithNoTimeDashed(startDate)} fwdDisabled={dateWithNoTimeDashed(currentDate) === dateWithNoTimeDashed(endDate)} firstName={selectedUserInfo?.firstName ?? undefined} compact={isLandscapePhone}/>), [
        currentDate,
        startDate,
        endDate,
        selectedUserInfo?.firstName,
        setCurrentDate,
        isLandscapePhone,
    ]);
    const renderSummaryHeader = useCallback(() => (<SummaryHeaderTitle date={currentDate} onDateChange={setCurrentDate} backDisabled={backDisabled} fwdDisabled={fwdDisabled}/>), [currentDate, backDisabled, fwdDisabled, setCurrentDate]);
    // It's important to note that the Tab.Screen children are intentionally
    // wrapped in a ScrollView. This allows the window's scroll bar to be
    // active, instead of having a nested scrollbar.
    // The phone admin list is that scroller. overflow:hidden on the column
    // (used so a client's Meal Map doesn't widen the page) stops iOS Safari
    // from scrolling it, so skip the clip while the list is showing.
    const clipPageOverflow = settingsHeader && adminView !== 'list';
    return (<ContentView>
      {settingsHeader ? (<ClientSettingsMenu open={settingsOpen} onClose={() => setSettingsOpen(false)} renderMenu={() => phoneAdmin ? (<SettingsView webMenu adminMenu/>) : (<SettingsView webMenu/>)}/>) : null}
      {adminView === 'client' && me ? (<ClientSettingsMenu open={switcherOpen} onClose={() => setSwitcherOpen(false)} renderMenu={() => (<UserInfo user={me} phone onClientPress={() => setSwitcherOpen(false)}/>)}/>) : null}
      <View style={[styles.container, clipPageOverflow && styles.containerClient]}>
        {/* Admin side panel: hidden on phones (#407, #411). */}
        {!shouldShowSidePanel({
            clientHeader,
            isLandscapePhone,
            isNarrowWindow,
        }) ? null : (<SidePanel onAddClientPress={isAdmin && !isAddingClient ? handleAddClientPress : undefined} onGenerateProgramPress={isAdmin
                ? () => navigation.navigate('GenerateProgram', {
                    selectedUserId: selectedUserId ?? undefined,
                })
                : undefined}/>)}
        <View style={[styles.content, clipPageOverflow && styles.contentClient]}>
          {isAddingClient && isAdmin && me ? (<ScrollView>
              <Suspense fallback={<TabFallback />}>
                <LazyProfileView key="add-client-profile" byUserId={me.id} onAddComplete={handleAddClientComplete} onCancel={handleCancelAdd}/>
              </Suspense>
            </ScrollView>) : adminView === 'list' && me ? (
        // One scrollport for the whole list. Names are static rows
        // (UserInfo `phone`) so a touch on iOS Safari pans this view.
        <ScrollView style={styles.adminPhoneListScroll} contentContainerStyle={styles.adminPhoneList}>
              <UserInfo user={me} phone/>
            </ScrollView>) : isProfileLoading ? (<PromptText prompt="Loading client details..."/>) : showSelectedUserFetchError ? (<PromptText prompt={'Error loading client data. Please select a client from the list.'}/>) : isSignedIn &&
            !isAdmin &&
            showClientMealArea &&
            selectedUserInfo &&
            me ? (<>
              <Tab.Navigator key={`client-${me.id}`} initialRouteName={clientTab} screenOptions={isLandscapePhone
                ? landscapeTabOptions
                : narrowClient
                    ? narrowClientTabOptions
                    : {
                        tabBarStyle: {
                            height: 50,
                            paddingTop: 10,
                            paddingBottom: 10,
                        },
                    }} screenListeners={({ navigation: _navigation }) => ({
                state: e => {
                    const state = e.data?.state;
                    if (state) {
                        const routeName = state.routes[state.index]?.name;
                        if (routeName) {
                            setClientTab(routeName);
                        }
                    }
                },
            })}>
                <Tab.Screen name="Profile" children={() => (<ScrollView>
                      <Suspense fallback={<TabFallback />}>
                        {/* Fluid cards/fields on phones, as in sign-up. */}
                        <ResponsiveFormProvider>
                          <LazyProfileView user={selectedUserInfo} key={`${selectedUserInfo.id}-${selectedUserInfo.updatedAt}`}/>
                        </ResponsiveFormProvider>
                      </Suspense>
                    </ScrollView>)} options={{
                tabBarIcon: ProfileIcon,
            }} listeners={{
                tabPress: () => {
                    setClientTab('Profile');
                },
            }}/>
                <Tab.Screen name="Meal Map" children={() => (<WithRotateHint>
                      <Suspense fallback={<TabFallback />}>
                        <LazyAdminMealMapView currentDate={currentDate}/>
                      </Suspense>
                    </WithRotateHint>)} options={{
                tabBarIcon: MealMapIcon,
                headerTitle: renderMealMapHeader,
            }} listeners={{
                tabPress: () => {
                    setClientTab('Meal Map');
                },
            }}/>
                <Tab.Screen name="Summary" children={() => (<WithRotateHint>
                      <Suspense fallback={<TabFallback />}>
                        <LazySummaryView userId={selectedUserInfo.id} currentDate={currentDate} _onChangeDate={setCurrentDate} clientFirstName={selectedUserInfo.firstName || ''} weekStartsOn={selectedUserInfo.weekStartsOn}/>
                      </Suspense>
                    </WithRotateHint>)} options={{
                tabBarIcon: SummaryIcon,
                headerTitle: renderSummaryHeader,
            }} listeners={{
                tabPress: () => {
                    setClientTab('Summary');
                },
            }}/>
                <Tab.Screen name="Trends" children={renderTrendsTab} options={{
                tabBarIcon: TrendsIcon,
            }} listeners={{
                tabPress: () => {
                    setClientTab('Trends');
                },
            }}/>
                <Tab.Screen name="Photos" children={() => (<Suspense fallback={<TabFallback />}>
                      <LazyPhotosView user={selectedUserInfo}/>
                    </Suspense>)} options={{
                tabBarIcon: PhotosIcon,
                headerRight: () => uploadPhotoButton(),
            }} listeners={{
                tabPress: () => {
                    setClientTab('Photos');
                },
            }}/>
              </Tab.Navigator>
              <PhotoUploadModal fullScreen={phoneSheet} visible={isUploadModalVisible} onClose={() => setIsUploadModalVisible(false)} userIdToUploadFor={selectedUserInfo.id}/>
            </>) : isSignedIn &&
            isAdmin &&
            isViewingSelectedUser &&
            selectedUserInfo ? (<>
              <Tab.Navigator key={selectedUserId} initialRouteName={currentTab} screenOptions={isLandscapePhone
                ? landscapeTabOptions
                : phoneAdmin
                    ? narrowClientTabOptions
                    : {
                        tabBarStyle: {
                            height: 50,
                            paddingTop: 10,
                            paddingBottom: 10, // Adds space below the icons/titles inside the tab bar
                        },
                    }} screenListeners={({ navigation: _navigation }) => ({
                state: e => {
                    // Update currentTab when user navigates between tabs
                    const state = e.data?.state;
                    if (state) {
                        const routeName = state.routes[state.index]?.name;
                        if (routeName) {
                            setCurrentTab(routeName);
                        }
                    }
                },
            })}>
                <Tab.Screen name="Profile" children={() => (<ScrollView>
                      <Suspense fallback={<TabFallback />}>
                        {/* Phone admins (#411): same single-column phone form
                    as the client's own Profile (view and edit). */}
                        <OptionalResponsiveFormProvider enabled={phoneAdmin}>
                          <LazyProfileView editCompletion={editingProfile ? editCompletion : undefined} user={selectedUserInfo} key={`${selectedUserInfo.id}-${selectedUserInfo.updatedAt}-${editingProfile}`}/>
                        </OptionalResponsiveFormProvider>
                      </Suspense>
                    </ScrollView>)} options={{
                tabBarIcon: ProfileIcon,
                headerRight: () => editProfileButton(),
            }} listeners={{
                tabPress: () => {
                    setCurrentTab('Profile');
                },
            }}/>
                {showAdminMealArea && (<Tab.Screen name="Meal Map" children={() => (<WithRotateHint>
                        <Suspense fallback={<TabFallback />}>
                          <LazyAdminMealMapView currentDate={currentDate}/>
                        </Suspense>
                      </WithRotateHint>)} options={{
                    tabBarIcon: MealMapIcon,
                    headerTitle: renderMealMapHeader,
                }} listeners={{
                    tabPress: () => {
                        setCurrentTab('Meal Map');
                    },
                }}/>)}
                {showAdminMealArea && (<Tab.Screen name="Summary" children={() => (<WithRotateHint>
                        <Suspense fallback={<TabFallback />}>
                          <LazySummaryView userId={selectedUserInfo.id} currentDate={currentDate} _onChangeDate={setCurrentDate} clientFirstName={selectedUserInfo.firstName || ''} weekStartsOn={selectedUserInfo.weekStartsOn}/>
                        </Suspense>
                      </WithRotateHint>)} options={{
                    tabBarIcon: SummaryIcon,
                    headerTitle: renderSummaryHeader,
                }} listeners={{
                    tabPress: () => {
                        setCurrentTab('Summary');
                    },
                }}/>)}
                {showAdminMealArea && (<Tab.Screen name="Trends" children={renderTrendsTab} options={{
                    tabBarIcon: TrendsIcon,
                }} listeners={{
                    tabPress: () => {
                        setCurrentTab('Trends');
                    },
                }}/>)}
                <Tab.Screen name="History" children={() => (<ScrollView>
                      <Suspense fallback={<TabFallback />}>
                        <LazyHistoryView user={selectedUserInfo} key={`History ${selectedUserInfo.id}`}/>
                      </Suspense>
                    </ScrollView>)} options={{
                tabBarIcon: NoteIcon,
                headerRight: () => addNoteButton(),
            }} listeners={{
                tabPress: () => {
                    setCurrentTab('History');
                },
            }}/>
                <Tab.Screen name="Photos" children={() => (<Suspense fallback={<TabFallback />}>
                      <LazyPhotosView user={selectedUserInfo}/>
                    </Suspense>)} options={{
                tabBarIcon: PhotosIcon,
                headerRight: () => uploadPhotoButton(),
            }} listeners={{
                tabPress: () => {
                    setCurrentTab('Photos');
                },
            }}/>
              </Tab.Navigator>
              {notePanelMounted && selectedUserInfo && me && (<NoteSidePanel fullScreen={phoneSheet} visible={notePanelVisible} byUser={me} forUser={selectedUserInfo} onSave={payload => {
                    createNote(payload).finally(() => setNotePanelVisible(false));
                }} onCancel={closeNotePanel} onAnimationComplete={handleNotePanelAnimationComplete}/>)}
              {selectedUserInfo && (<PhotoUploadModal fullScreen={phoneSheet} visible={isUploadModalVisible} onClose={() => setIsUploadModalVisible(false)} userIdToUploadFor={selectedUserInfo.id}/>)}
            </>) : (<PromptText prompt={!isSignedIn
                ? "Welcome to Abbi's Way\n\nPlease sign in"
                : isAdmin
                    ? `Hi, ${me.firstName}.\n\nPlease select a client to administer or add a client.`
                    : me && selectedUserInfo?.programType == null
                        ? `Hi, ${me.firstName}.\n\nYour program is not set up yet. Please contact your coach if you need help.`
                        : 'Something went wrong loading your account. Try signing out and back in.'}/>)}
        </View>
      </View>
    </ContentView>);
};
/**
 * Phone-width client tabs: five tabs share the width (flex, no min width),
 * icon above a short label, so Trends and Photos stay on screen.
 */
const narrowClientTabOptions = {
    tabBarLabelPosition: 'below-icon',
    tabBarStyle: {
        height: 58,
        paddingTop: 6,
        paddingBottom: 6,
    },
    tabBarItemStyle: {
        flex: 1,
        minWidth: 0,
        paddingHorizontal: 0,
    },
    tabBarLabelStyle: {
        fontSize: 11,
    },
};
/**
 * Sideways phone: slim tab header and tab bar, label beside
 * the icon, so the Meal Map, Summary and Trends get most of the ~390px.
 */
const shortLandscapeTabOptions = {
    headerStyle: { height: 44 },
    // Let the title (e.g. the Meal Map week picker) fill the row instead of
    // shrinking to its text and truncating.
    headerTitleContainerStyle: { flex: 1 },
    headerStatusBarHeight: 0,
    tabBarLabelPosition: 'beside-icon',
    tabBarStyle: {
        height: 40,
        paddingTop: 2,
        paddingBottom: 2,
    },
    tabBarItemStyle: {
        flex: 1,
        minWidth: 0,
        paddingHorizontal: 0,
    },
    tabBarLabelStyle: {
        fontSize: 12,
    },
};
const tabFallbackStyles = StyleSheet.create({
    withHint: {
        flex: 1,
    },
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
    },
});
const styles = StyleSheet.create({
    container: {
        flex: 1,
        flexDirection: 'row',
    },
    content: {
        flex: 1,
    },
    // Clip page-level sideways scroll for clients; wide content (e.g. the Meal
    // Map grid) scrolls inside its own ScrollView instead.
    containerClient: {
        width: '100%',
        overflow: 'hidden',
    },
    contentClient: {
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
    },
    adminPhoneListScroll: {
        flex: 1,
        minHeight: 0,
    },
    adminPhoneList: {
        paddingHorizontal: 16,
    },
    headerButtonPhone: {
        marginRight: 8,
    },
    headerButton: {
        marginRight: 20,
    },
    stubTabContent: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
